'use client';

import React, { useEffect, useRef, useState } from 'react';
import * as Dialog from './design-dialog';
import { ArrowDown, CaretDown, X } from '@phosphor-icons/react';
import { CheckIcon, MinusIcon, PlusIcon } from '@/components/icons';
import type {
  StorefrontContact,
  StorefrontScene,
  StorefrontStudio,
} from '@/lib/api/storefront-media';
import studioScene from './product-detail-studio.json';
import { useProductDetailMotion } from './use-product-detail-motion';

export type DetailProduct = {
  id: number;
  name: string;
  price: number;
  image: string;
  studioRetouched?: boolean;
  studio?: StorefrontStudio;
  contact?: StorefrontContact;
  weight?: string | null;
  ingredients?: string | null;
  description?: string | null;
  allergens?: string | null;
  calories?: number | null;
  proteins?: number | null;
  fats?: number | null;
  carbs?: number | null;
  is_popular?: boolean;
  is_new?: boolean;
  spiciness?: string | null;
  categories: { id: number; name: string }[];
};

export type DetailScene = StorefrontScene;
export type CartAddition = { id: number; quantity: number };
type Props = {
  product: DetailProduct | null;
  category: string;
  scene?: DetailScene;
  sauces: DetailProduct[];
  cart: Record<string, number>;
  onClose: () => void;
  onAdd: (items: CartAddition[]) => boolean;
  available?: boolean;
  onBack?: () => void;
  parentName?: string;
  extraContent?: React.ReactNode;
};
const money = (value: number) => `${value.toLocaleString('ru-RU')} ₽`;
const quantityLimit = 99;

function ProductCutout({ product }: { product: DetailProduct }) {
  const frame = product.studio;
  const contact = product.contact;
  if (!frame)
    return (
      <img
        className="pd-cutout-fallback"
        src={product.image}
        alt={product.name.trim()}
        draggable={false}
      />
    );
  const [x, y, right, bottom] = frame.bounds;
  const width = right - x;
  const height = bottom - y;
  const imageStyle = {
    width: `${(frame.width / width) * 100}%`,
    left: `${(-x / width) * 100}%`,
    top: `${(-y / height) * 100}%`,
  } as React.CSSProperties;
  return (
    <div
      className="pd-cutout-frame"
      style={{ '--cutout-ratio': width / height } as React.CSSProperties}
    >
      {/* Two separate <svg>s: WebKit (every iOS browser) ignores CSS filter on SVG
          children, so the blur lives on the outer element, where it works everywhere. */}
      {contact?.path && (
        <>
          <svg
            className="pd-ground-shadow pd-ground-ambient"
            viewBox={`0 0 ${contact.width} ${contact.height}`}
            preserveAspectRatio="none"
            aria-hidden="true"
            focusable="false"
          >
            <path d={contact.path} strokeWidth={contact.width * 0.036} />
          </svg>
          <svg
            className="pd-ground-shadow pd-ground-contact"
            viewBox={`0 0 ${contact.width} ${contact.height}`}
            preserveAspectRatio="none"
            aria-hidden="true"
            focusable="false"
          >
            <path d={contact.path} strokeWidth={contact.width * 0.008} />
          </svg>
        </>
      )}
      <img
        className="pd-cutout-product"
        src={product.image}
        alt={product.name.trim()}
        draggable={false}
        style={imageStyle}
      />
    </div>
  );
}

export function ProductDetail(props: Props) {
  return (
    <Dialog.Root
      open={!!props.product}
      onOpenChange={(open) => {
        if (!open) props.onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="pd-overlay" />
        {props.product && (
          <ProductDetailBody key={props.product.id} {...props} product={props.product} />
        )}
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function ProductDetailBody({
  product,
  category,
  scene,
  sauces,
  cart,
  onClose,
  onAdd,
  available = true,
  onBack,
  parentName,
  extraContent,
}: Props & { product: DetailProduct }) {
  const [quantity, setQuantity] = useState(1);
  const [extras, setExtras] = useState<number[]>([]);
  const [allSauces, setAllSauces] = useState(false);
  const [compact, setCompact] = useState(false);
  const [added, setAdded] = useState(false);
  const [lastAddition, setLastAddition] = useState<{ quantity: number; total: number } | null>(
    null
  );
  const [dragOffset, setDragOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const { rootRef, scrollRef, heroRef, titleRef, purchaseRef, scrollTo } =
    useProductDetailMotion(setCompact);
  const extrasRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const dragRef = useRef<{ y: number; moved: boolean } | null>(null);
  const suppressHandleClick = useRef(false);
  const offerSauces =
    !product.categories.some((c) => c.id === 7 || c.id === 20) && sauces.length > 0;
  const selectedSauces = offerSauces ? sauces.filter((s) => extras.includes(s.id)) : [];
  const maxQuantity = Math.max(
    0,
    Math.min(
      quantityLimit - (cart[product.id] || 0),
      ...selectedSauces.map((s) => quantityLimit - (cart[s.id] || 0))
    )
  );
  const effectiveQuantity = Math.max(1, Math.min(quantity, maxQuantity));
  const extraPrice = selectedSauces.reduce((sum, s) => sum + s.price, 0);
  const total = (product.price + extraPrice) * effectiveQuantity;
  const details = (product.description || '').trim();
  const hasNutrition = [product.calories, product.proteins, product.fats, product.carbs].some(
    (n) => n != null
  );
  const nutrition = [
    { value: product.calories, label: 'ккал' },
    { value: product.proteins, label: 'белки, г' },
    { value: product.fats, label: 'жиры, г' },
    { value: product.carbs, label: 'углеводы, г' },
  ];
  // Use the scene's actual lower-edge color throughout the dialog. Its palette
  // stays intact in either site theme. Cutouts share a lit studio set.
  const canvas = scene ? scene.canvas || scene.color : studioScene.canvas;
  const lightInk = (scene?.ink || studioScene.ink).toLowerCase() === '#ffffff';
  // Both scene layers use the same food-centered camera. Transparent margins in
  // the catalog masters must not push the food to the top of a mobile viewport.
  const [sceneLeft, sceneTop, sceneRight, sceneBottom] = scene?.bounds || [0, 0, 1, 1];
  const sceneCenterX = scene ? (sceneLeft + sceneRight) / 2 / scene.width : 0.5;
  const sceneCenterY = scene ? (sceneTop + sceneBottom) / 2 / scene.height : 0.5;
  const sceneHeightScale = scene ? scene.height / (sceneBottom - sceneTop) : 1;

  const style = {
    '--pd-scene': canvas,
    '--pd-scene-ink': scene?.ink || studioScene.ink,
    '--pd-scene-x': `${-sceneCenterX * 100}%`,
    '--pd-scene-y': `${-sceneCenterY * 100}%`,
    '--pd-scene-height-scale': sceneHeightScale,
    '--pd-scene-width-scale': scene ? scene.width / (sceneRight - sceneLeft) : 1,

    '--pd-photo': `url("${scene?.image || product.image}")`,
    '--pd-action': lightInk ? '#ffffff' : '#7A42FE',
    '--pd-action-ink': lightInk ? '#6731df' : '#ffffff',
    '--pd-focus': lightInk ? '#ffffff' : '#6731df',
    '--pd-drag': `${dragOffset}px`,
  } as React.CSSProperties;

  useEffect(() => {
    if (!added) return;
    const timer = window.setTimeout(() => setAdded(false), 1400);
    return () => window.clearTimeout(timer);
  }, [added]);

  const scrollToSauces = () => {
    scrollTo(extrasRef.current);
    extrasRef.current?.querySelector<HTMLButtonElement>('button')?.focus({ preventScroll: true });
  };
  const addSelection = () => {
    if (!available || !maxQuantity || added) return;
    const accepted = onAdd([
      { id: product.id, quantity: effectiveQuantity },
      ...selectedSauces.map((s) => ({ id: s.id, quantity: effectiveQuantity })),
    ]);
    if (!accepted) return;
    setLastAddition({ quantity: effectiveQuantity, total });
    setAdded(true);
  };
  const finishDrag = (event: React.PointerEvent<HTMLButtonElement>, cancelled = false) => {
    const distance = dragRef.current ? event.clientY - dragRef.current.y : 0;
    suppressHandleClick.current = !!dragRef.current?.moved;
    dragRef.current = null;
    setDragging(false);
    setDragOffset(0);
    if (!cancelled && distance > 100) onClose();
  };

  return (
    <Dialog.Content
      ref={rootRef}
      className={`product-detail${scene ? ' has-scene' : ' has-cutout'}${compact ? ' is-scrolled' : ''}`}
      style={style}
      data-product-id={product.id}
      {...(!details ? { 'aria-describedby': undefined } : {})}
      data-dragging={dragging || undefined}
      onOpenAutoFocus={(event) => {
        openerRef.current = document.activeElement as HTMLElement;
        event.preventDefault();
        closeRef.current?.focus({ preventScroll: true });
      }}
      onCloseAutoFocus={(event) => {
        event.preventDefault();
        if (openerRef.current?.isConnected) openerRef.current.focus({ preventScroll: true });
        else
          document
            .querySelector<HTMLButtonElement>(`[data-product-id="${product.id}"] .food-button`)
            ?.focus({ preventScroll: true });
      }}
    >
      {onBack && (
        <button className="pd-back" onClick={onBack}>
          ← К сету {parentName}
        </button>
      )}
      <div className="pd-atmosphere" aria-hidden="true" />
      <div className="pd-toolbar">
        <Dialog.Close
          ref={closeRef}
          className="pd-round-button pd-close"
          aria-label="Закрыть карточку товара"
        >
          <X size={23} />
        </Dialog.Close>
        <div className="pd-compact-title" aria-hidden="true">
          <strong>{product.name.trim()}</strong>
          <span>{product.weight}</span>
        </div>
      </div>
      <button
        type="button"
        className="pd-drag-handle"
        aria-label="Закрыть карточку или потянуть вниз"
        onClick={(event) => {
          if (!suppressHandleClick.current || event.detail === 0) onClose();
        }}
        onPointerDown={(event) => {
          if (event.button !== 0) return;
          dragRef.current = { y: event.clientY, moved: false };
          suppressHandleClick.current = false;
          event.currentTarget.setPointerCapture(event.pointerId);
          setDragging(true);
        }}
        onPointerMove={(event) => {
          if (!dragRef.current) return;
          const distance = event.clientY - dragRef.current.y;
          if (Math.abs(distance) > 6) dragRef.current.moved = true;
          setDragOffset(Math.max(0, Math.min(300, distance)));
        }}
        onPointerUp={(event) => finishDrag(event)}
        onPointerCancel={(event) => finishDrag(event, true)}
      >
        <span />
      </button>

      <div className="pd-scroll" ref={scrollRef}>
        <div className="pd-hero" ref={heroRef}>
          <div className="pd-hero-viewport">
            <div className="pd-hero-motion">
              {scene ? (
                <>
                  <img
                    className="pd-scene-image pd-scene-backdrop"
                    src={scene.image}
                    alt=""
                    aria-hidden="true"
                    draggable={false}
                  />
                  <img
                    className="pd-scene-image pd-scene-product"
                    src={scene.foreground}
                    alt={product.name.trim()}
                    draggable={false}
                  />
                </>
              ) : (
                <div className="pd-cutout-stage">
                  <div
                    className="pd-studio-set"
                    data-studio-kind={
                      product.categories.some((c) => c.id === 7)
                        ? 'sauce'
                        : product.categories.some((c) => c.id === 2)
                          ? 'set'
                          : product.categories.some((c) => c.id === 1)
                            ? 'roll-dog'
                            : product.categories.some((c) => c.id === 21)
                              ? 'sushi'
                              : 'food'
                    }
                  >
                    <img
                      className="pd-studio-backdrop"
                      src={studioScene.image}
                      alt=""
                      aria-hidden="true"
                      draggable={false}
                    />
                    <ProductCutout product={product} />
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="pd-hero-bottom">
            <span className="pd-hero-note">{product.weight}</span>
            {offerSauces && (
              <button
                className="pd-sauce-shortcut"
                onClick={scrollToSauces}
                tabIndex={compact ? -1 : undefined}
              >
                <img src={sauces.find((s) => s.id === 6)?.image || sauces[0].image} alt="" />
                <span>Добавить соус</span>
                <ArrowDown size={16} />
              </button>
            )}
          </div>
        </div>

        <div className="pd-body">
          <div className="pd-intro">
            <div className="pd-intro-copy">
              <div className="pd-eyebrow">
                <span>{category}</span>
                {product.is_popular ? (
                  <span className="pd-label">Хит</span>
                ) : product.is_new ? (
                  <span className="pd-label">Новинка</span>
                ) : null}
              </div>
              <Dialog.Title ref={titleRef} className="pd-title">
                {product.name.trim()}
              </Dialog.Title>
              <div className="pd-meta">
                <span>{product.weight}</span>
                {product.spiciness === 'spicy' && (
                  <span className="pd-spicy">
                    <img src="/storefront/sticker-pepper-v3.webp" alt="" />
                    Острый
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="pd-details">
            {product.ingredients && (
              <section
                className="pd-section pd-composition pd-reveal"
                aria-labelledby={`pd-ingredients-${product.id}`}
              >
                <h3 id={`pd-ingredients-${product.id}`}>Что внутри</h3>
                <p>{product.ingredients.trim()}</p>
              </section>
            )}

            {extraContent}
            {offerSauces && (
              <section
                className="pd-section pd-extras pd-reveal"
                ref={extrasRef}
                aria-labelledby={`pd-sauces-${product.id}`}
              >
                <div className="pd-section-heading">
                  <h3 id={`pd-sauces-${product.id}`}>Ещё вкуснее вместе</h3>
                  <span>Соусы к роллам</span>
                </div>
                <div className={`pd-sauces${allSauces ? ' show-all' : ''}`}>
                  {(allSauces ? sauces : sauces.slice(0, 3)).map((sauce) => {
                    const selected = extras.includes(sauce.id);
                    const unavailable = (cart[sauce.id] || 0) >= quantityLimit;
                    return (
                      <button
                        type="button"
                        key={sauce.id}
                        className={`pd-sauce${selected ? ' is-selected' : ''}`}
                        aria-pressed={selected}
                        aria-label={`${sauce.name.trim()}, ${money(sauce.price)}${selected ? ', выбран' : ''}`}
                        disabled={unavailable && !selected}
                        onClick={() => {
                          setAdded(false);
                          setExtras((ids) =>
                            selected ? ids.filter((id) => id !== sauce.id) : [...ids, sauce.id]
                          );
                        }}
                      >
                        <span className="pd-sauce-check" aria-hidden="true">
                          {selected ? <CheckIcon size={13} weight="bold" /> : <PlusIcon size={13} />}
                        </span>
                        <img src={sauce.image} alt="" loading="lazy" draggable={false} />
                        <strong>{sauce.name.replace(/^Соус\s+/i, '').trim()}</strong>
                        <span className="pd-sauce-weight">{sauce.weight}</span>
                        <span className="pd-sauce-price">+ {money(sauce.price)}</span>
                      </button>
                    );
                  })}
                </div>
                {sauces.length > 3 && (
                  <button
                    className="pd-more-sauces"
                    aria-expanded={allSauces}
                    onClick={() => setAllSauces(!allSauces)}
                  >
                    {allSauces ? 'Свернуть' : 'Все дополнения'}
                    <CaretDown size={14} />
                  </button>
                )}
                {selectedSauces.length > 0 && (
                  <p className="pd-extras-note">По одному выбранному соусу к каждой порции.</p>
                )}
              </section>
            )}

            {hasNutrition && (
              <section
                className="pd-section pd-nutrition pd-reveal"
                aria-labelledby={`pd-nutrition-${product.id}`}
              >
                <h3 id={`pd-nutrition-${product.id}`}>Пищевая ценность</h3>
                <dl>
                  {nutrition.map((n) => (
                    <div key={n.label}>
                      <dd>{n.value == null ? '—' : n.value.toLocaleString('ru-RU')}</dd>
                      <dt>{n.label}</dt>
                    </div>
                  ))}
                </dl>
              </section>
            )}

            {(details || product.allergens) && (
              <section
                className="pd-section pd-more-details pd-reveal"
                aria-labelledby={`pd-details-${product.id}`}
              >
                <h3 id={`pd-details-${product.id}`}>О продукте</h3>
                {details && (
                  <Dialog.Description>{details}</Dialog.Description>
                )}
                {product.allergens && (
                  <p>
                    <strong>Аллергены: </strong>
                    {product.allergens}
                  </p>
                )}
              </section>
            )}
          </div>
        </div>
      </div>

      <div className="pd-purchase" ref={purchaseRef}>
        {selectedSauces.length > 0 && (
          <div className="pd-purchase-summary">
            <span>С дополнениями{effectiveQuantity > 1 ? ` · ×${effectiveQuantity}` : ''}</span>
            <span>+ {money(extraPrice * effectiveQuantity)}</span>
          </div>
        )}
        <div className="pd-purchase-row">
          <div className="pd-quantity" aria-label="Количество порций">
            <button
              aria-label="Уменьшить количество"
              disabled={effectiveQuantity <= 1 || added}
              onClick={() => setQuantity(effectiveQuantity - 1)}
            >
              <MinusIcon size={17} />
            </button>
            <output aria-live="polite">
              {added && lastAddition ? lastAddition.quantity : effectiveQuantity}
            </output>
            <button
              aria-label="Увеличить количество"
              disabled={effectiveQuantity >= maxQuantity || added}
              onClick={() => setQuantity(effectiveQuantity + 1)}
            >
              <PlusIcon size={17} />
            </button>
          </div>
          <button
            className={`pd-add${added ? ' is-added' : ''}`}
            disabled={!available || maxQuantity === 0 || added}
            onClick={addSelection}
          >
            <span>
              {!available ? (
                'Скоро в меню'
              ) : added ? (
                <>
                  <CheckIcon size={19} weight="bold" />
                  Добавлено
                </>
              ) : maxQuantity === 0 ? (
                'Уже в корзине'
              ) : (
                'В корзину'
              )}
            </span>
            <b>{money(added && lastAddition ? lastAddition.total : total)}</b>
          </button>
        </div>
        {maxQuantity === 0 && (
          <p className="pd-limit" role="status">
            В корзине уже максимальное количество этого товара или соуса.
          </p>
        )}
      </div>
    </Dialog.Content>
  );
}
